import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { RichText } from "../../../components/RichText";
import { ProductCompareTheme } from "../../../constants";
import { ISafeHTML, WebPageURL, isoSafeHTML } from "../../../models/nominals";
import {
    IAttributeOptionGroup,
    ICompareTile,
    IGroupColumnsHeader,
    ITileID,
} from "../../../models/product-compare";
import { useRequestPath } from "../../../utils/urls";
import { SizeSelector } from "../ProductCompareSizeSelector";
import { ProductSelectionGridColumn } from "../ProductSelectionGridColumn";
import { ProductCompareAnalyticsProvider } from "../analytics";
import { useViewItemListTracking } from "../hooks/useViewItemListTracking";
import { getHighestVisibleAttributeCount } from "../utility";

import styles from "./ProductSelectionGridContent.module.scss";

type IProps = Immutable<{
    header: ISafeHTML;
    productComparePageURL: WebPageURL | null;
    tiles: readonly ICompareTile[];
    showStartingAt?: boolean;
    showRating?: boolean;
    ctaText?: string;
    selectedTileIDs: readonly ITileID[];
    preselectedSize: string | null;
    priceSelectValue: string;
    financingLink: WebPageURL;
    sizeAttributeOptionGroup: IAttributeOptionGroup | null;
    theme: ProductCompareTheme;
    footer: ISafeHTML | null;
    groupHeader?: IGroupColumnsHeader | null;
    showSizeSelector?: boolean | null;
    addToSelected: (tileID: ITileID) => void;
    removeFromSelected: (tileID: ITileID) => void;
    showModal: () => void;
}>;

export const ProductSelectionGrid = (props: IProps): React.ReactNode => {
    const ctaText = props.ctaText ? props.ctaText : t`Compare All Mattresses`;

    const buildTileIDString = (): string => {
        const tileIDs = props.tiles.map((tile) => {
            return tile.product_compare_tile.id;
        });

        return tileIDs.join(",");
    };

    const buildHeaderContent = (url: string): ISafeHTML => {
        if (props.productComparePageURL) {
            const link = `<a href="${url}">${ctaText}</a>`;
            return isoSafeHTML.wrap(`${props.header}${link}`);
        }
        return isoSafeHTML.wrap(`${props.header}`);
    };

    const requestPath = useRequestPath();
    const isThemeOne = props.theme === ProductCompareTheme.THEME_ONE;
    const url = `${
        props.productComparePageURL
    }?tiles=${buildTileIDString()}&return_url=${encodeURIComponent(
        requestPath,
    )}`;
    const activeTiles = props.tiles.filter(
        (tile) => tile.is_promo_period_active !== false,
    );
    const allTilesLength = activeTiles.length;
    const groupedColumns =
        props.groupHeader && isThemeOne
            ? activeTiles.slice(Number(props.groupHeader.numColumns) * -1)
            : null;
    const firstGroupTiles =
        props.groupHeader && isThemeOne
            ? activeTiles.slice(
                  0,
                  allTilesLength - Number(props.groupHeader.numColumns),
              )
            : activeTiles;

    const headerClassNames = classNames({
        [styles.header]: true,
        [props.theme || ""]: isThemeOne,
    });
    const tilesClassNames = classNames({
        [styles.tiles]: true,
        [styles.tilesThemeOne]: isThemeOne,
    });
    const rootClasses = classNames({
        [styles.root]: true,
        [styles.rootThemeOne]: isThemeOne,
    });
    const highestVisibleAttributeCount =
        getHighestVisibleAttributeCount(firstGroupTiles);
    const { listMetadata, viewedProducts } = useViewItemListTracking(
        activeTiles,
        props.sizeAttributeOptionGroup ? props.priceSelectValue : null,
        props.header,
        "Product Selection Grid",
    );

    return (
        <ProductCompareAnalyticsProvider
            listMetadata={listMetadata}
            viewedProducts={viewedProducts}
        >
            <div className={rootClasses}>
                <header className={headerClassNames}>
                    <RichText html={buildHeaderContent(url)} />
                </header>
                {props.sizeAttributeOptionGroup && (
                    <SizeSelector
                        sizeAttributeOptionGroup={
                            props.sizeAttributeOptionGroup
                        }
                        preselectedSize={props.preselectedSize}
                        showSizeSelector={props.showSizeSelector}
                        theme={props.theme}
                    />
                )}
                <div className={tilesClassNames}>
                    {activeTiles.length === 0 && <LoadingSpinner />}
                    <div className={styles.groupTilesNormal}>
                        {props.groupHeader?.text.content && (
                            <div aria-hidden="true" className="sr-only">
                                <RichText
                                    className={`${styles.specialColumnsHeader} ${
                                        props.groupHeader.text.alignment || ""
                                    }`}
                                    html={props.groupHeader.text.content}
                                />
                            </div>
                        )}
                        <div className="tiles">
                            {firstGroupTiles.map((tile) => (
                                <ProductSelectionGridColumn
                                    key={`tile-${tile.product_compare_tile.id}`}
                                    tile={tile}
                                    highestAttributeCount={
                                        highestVisibleAttributeCount
                                    }
                                    {...props}
                                />
                            ))}
                        </div>
                    </div>
                    {isThemeOne && (
                        <div className={styles.groupTilesSpecial}>
                            {props.groupHeader?.text.content && (
                                <RichText
                                    className={`${styles.specialColumnsHeader} ${
                                        props.groupHeader.text.alignment || ""
                                    }`}
                                    html={props.groupHeader.text.content}
                                />
                            )}
                            <div className="tiles">
                                {groupedColumns?.map((tile) => (
                                    <ProductSelectionGridColumn
                                        key={`tile-${tile.product_compare_tile.id}`}
                                        tile={tile}
                                        {...props}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {props.productComparePageURL && (
                    <a
                        className="button button--secondary button--compare-all al-compareallmattresses-whitebutton"
                        href={url}
                    >
                        {ctaText}
                    </a>
                )}
                {props.footer && (
                    <footer>
                        <RichText html={props.footer} />
                    </footer>
                )}
            </div>
        </ProductCompareAnalyticsProvider>
    );
};
